import { useTranslation } from "react-i18next"

import type { MyUsage } from "@/api/fair-use"

import { Lock } from "@/components/icons"

import { lockedPlanFeatures, planFeatureLabelKey } from "../fair-use-plan-utils"
import { useFairUsePlanConfig } from "../hooks/use-plan-entitlements"

type PlanSummaryProps = {
  usage: MyUsage
}

/**
 * The plan half of the budget popover (fair use round 2, #3087): which plan
 * the user is on, and what that plan does NOT include.
 *
 * It sits beside the month's spend rather than in a separate surface because
 * the two answer the same question from opposite sides — "why can't I do this
 * right now" is either "the money ran out" or "your plan never had it", and a
 * user who has just been refused must not have to find out which by trying
 * both. `routeFairUseError` opens this popover for a `plan_upgrade_required`
 * refusal for exactly that reason.
 *
 * Two sources, in priority order: `/api/me/usage`'s `plan_entitlements` (the
 * authoritative per-user row) then `/api/config`'s `fair_use` block (already
 * cached, and present on surfaces that have not fetched a usage snapshot).
 * With neither — fair use off, or an older backend — the whole block renders
 * nothing, which is also what a pre-round-2 popover did.
 */
export function PlanSummary({ usage }: PlanSummaryProps) {
  const { t } = useTranslation()
  const fairUseConfig = useFairUsePlanConfig()

  const planId = usage.tier ?? fairUseConfig.plan ?? undefined
  const entitlements = usage.plan_entitlements ?? fairUseConfig.entitlements
  const locked = lockedPlanFeatures(entitlements)

  if (!planId && locked.length === 0) return null

  return (
    <div className="flex flex-col gap-1.5" data-testid="fair-use-plan-summary">
      {planId && (
        <p className="text-muted-foreground text-xs" data-testid="fair-use-popover-plan">
          {t("fairUse.popover.plan", {
            plan: t(`fairUse.plans.${planId}`, { defaultValue: planId }),
          })}
        </p>
      )}
      {locked.length > 0 && (
        <div className="flex flex-col gap-1" data-testid="fair-use-plan-locked">
          <p className="text-muted-foreground text-xs font-semibold">
            {t("fairUse.plan.lockedTitle", { defaultValue: "Not included in your plan" })}
          </p>
          <ul className="flex flex-wrap gap-1">
            {locked.map((item) => (
              <li
                key={item.feature}
                className="border-border text-muted-foreground flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]"
              >
                <Lock aria-hidden="true" className="size-2.5" />
                {t(planFeatureLabelKey(item.feature), { defaultValue: item.feature })}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
